import { useNavigate } from "react-router-dom";
import { Footer } from "../components/footer";
import { Header } from "../components/header";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import useBlogPosts from "../hooks/useBlogPosts";
import { PageLoadingShimmer } from "../components/page-loading";

function getAuthorInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Blog() {
  const navigate = useNavigate();
  const { blogPosts, pageLoading } = useBlogPosts();
  const [filterCategory, setFilterCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  console.log({ blogPosts });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const featuredPost =
    blogPosts.find((post) => post.isFeatured) || blogPosts[0];

  const categories = useMemo(
    () => [...new Set(blogPosts.map((post) => post.category).filter(Boolean))],
    [blogPosts],
  );

  const categoryCounts = useMemo(() => {
    const counts = { all: blogPosts.length };
    blogPosts.forEach((post) => {
      counts[post.category] = (counts[post.category] || 0) + 1;
    });
    return counts;
  }, [blogPosts]);

  const filteredPosts = useMemo(() => {
    let posts = blogPosts.filter((post) => !post.isFeatured);

    if (filterCategory !== "all") {
      posts = posts.filter((post) => post.category === filterCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      posts = posts.filter(
        (post) =>
          post.title?.toLowerCase().includes(query) ||
          post.shortDescription?.toLowerCase().includes(query),
      );
    }

    return posts;
  }, [filterCategory, searchQuery, blogPosts]);

  const openPost = (postId) => {
    navigate(`/blog/${postId}`);
  };

  return (
    <div className='blog-container'>
      <Header />
      {pageLoading ? (
        <PageLoadingShimmer />
      ) : (
        <main className='blog-page'>
          <section className='blog-page__intro'>
            <span className='blog-page__eyebrow'>Read Our Blog</span>
            <h1>Browse Our Resources</h1>
            <p>
              We share tips, recipes, and insights from food lovers and industry
              leaders. For real.
            </p>
          </section>

          {featuredPost ? (
            <section
              className='blog-page__hero'
              onClick={() => openPost(featuredPost.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  openPost(featuredPost.id);
                }
              }}
            >
              <img
                src={featuredPost.blogImage}
                alt={featuredPost.title}
                className='blog-page__hero-image'
              />
              <div className='blog-page__hero-overlay'>
                <div className='blog-page__hero-top'>
                  <div>
                    <h2>{featuredPost.title}</h2>
                    <p>{featuredPost.shortDescription}</p>
                  </div>
                  <span className='blog-page__hero-arrow' aria-hidden='true'>
                    <i className='bi bi-arrow-up-right'></i>
                  </span>
                </div>
                <div className='blog-page__hero-meta'>
                  <div className='blog-page__hero-author'>
                    <span className='blog-page__avatar'>
                      {getAuthorInitials(featuredPost.author)}
                    </span>
                    <span>{featuredPost.author}</span>
                    <span className='blog-page__meta-dot'>·</span>
                    <i className='bi bi-calendar3'></i>
                    <span>
                      {featuredPost.createdAt
                        ? moment(featuredPost.createdAt).format("MMM D, YYYY")
                        : "-"}
                    </span>
                  </div>
                  <div className='blog-page__hero-tags'>
                    {featuredPost.category ? (
                      <span className='blog-page__tag'>
                        {featuredPost.category}
                      </span>
                    ) : null}
                    <span className='blog-page__tag'>
                      {featuredPost.time} min read
                    </span>
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          <section className='blog-page__toolbar'>
            <nav className='blog-page__filters' aria-label='Blog categories'>
              <button
                type='button'
                className={filterCategory === "all" ? "active" : ""}
                onClick={() => setFilterCategory("all")}
              >
                All
                <span className='blog-page__filter-count'>
                  {categoryCounts.all || 0}
                </span>
              </button>
              {categories.map((category) => (
                <button
                  type='button'
                  key={category}
                  className={filterCategory === category ? "active" : ""}
                  onClick={() => setFilterCategory(category)}
                >
                  {category}
                  <span className='blog-page__filter-count'>
                    {categoryCounts[category] || 0}
                  </span>
                </button>
              ))}
            </nav>
            <label className='blog-page__search'>
              <input
                type='search'
                placeholder='Search blog...'
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
              <i className='bi bi-search'></i>
            </label>
          </section>

          <section className='blog-page__grid'>
            {filteredPosts.length ? (
              filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className='blog-page__card'
                  onClick={() => openPost(post.id)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      openPost(post.id);
                    }
                  }}
                >
                  <div className='blog-page__card-image'>
                    <img src={post.blogImage} alt={post.title} />
                  </div>
                  <div className='blog-page__card-body'>
                    {post.category ? (
                      <span className='blog-page__card-category'>
                        {post.category}
                      </span>
                    ) : null}
                    <h3>{post.title}</h3>
                    <p>{post.shortDescription}</p>
                    <div className='blog-page__card-footer'>
                      <div className='blog-page__card-author'>
                        <span className='blog-page__avatar blog-page__avatar--small'>
                          {getAuthorInitials(post.author)}
                        </span>
                        <span>{post.author}</span>
                      </div>
                      <span>{post.time} min read</span>
                    </div>
                  </div>
                </article>
              ))
            ) : (
              <div className='blog-page__empty'>
                <h3>No articles found</h3>
                <p>
                  {filterCategory !== "all" || searchQuery.trim()
                    ? "Try a different category or search term."
                    : "Check back later for new content."}
                </p>
              </div>
            )}
          </section>
        </main>
      )}
      <Footer />
    </div>
  );
}
